// City configuration for NextBus Greece
// NOTE: This configuration must stay in sync with src/api-config.ts
// Both files define the same cities with Athens as default

export type CityId = 'athens' | 'thessaloniki'

export interface CityConfig {
  id: CityId
  name: string
  nameGreek: string
  authority: string
  authorityGreek: string
  baseUrl: string
  appName: string
  appNameShort: string
  domain: string
  subdomains: string[] // subdomains that map to this city
}

export const CITIES: Record<CityId, CityConfig> = {
  athens: {
    id: 'athens',
    name: 'Athens',
    nameGreek: 'Αθήνα',
    authority: 'OASA',
    authorityGreek: 'ΟΑΣΑ',
    baseUrl: 'https://telematics.oasa.gr',
    appName: 'NextBus Athens',
    appNameShort: 'Athens',
    domain: 'athensbus.info',
    subdomains: ['ath']
  },
  thessaloniki: {
    id: 'thessaloniki',
    name: 'Thessaloniki',
    nameGreek: 'Θεσσαλονίκη',
    authority: 'OASTH',
    authorityGreek: 'ΟΑΣΘ',
    baseUrl: 'https://oseth.com.gr',
    appName: 'NextBus Thessaloniki',
    appNameShort: 'Thessaloniki',
    domain: 'thessbus.info',
    subdomains: ['skg']
  }
}

// Default city: Athens (must match src/api-config.ts)
export const DEFAULT_CITY: CityId = 'athens'

/**
 * Check if we're on the landing page (no city subdomain)
 * Returns true for: localhost, nextbus.com.gr, www.nextbus.com.gr
 * Returns false for: skg.localhost, ath.localhost, skg.nextbus.com.gr
 */
export function isLandingPage(hostname: string = window.location.hostname): boolean {
  const parts = hostname.toLowerCase().split('.')
  
  // No subdomain cases: localhost, example.com
  if (parts.length <= 1) return true
  
  // Check if first part is a known city subdomain
  const subdomain = parts[0]
  
  // www is not a city subdomain, treat as landing page
  if (subdomain === 'www') return true
  
  // Check if it's a city subdomain
  for (const city of Object.values(CITIES)) {
    if (city.subdomains.includes(subdomain)) {
      return false // It's a city-specific subdomain
    }
  }
  
  // Unknown subdomain or no subdomain = landing page
  return true
}

/**
 * Detect city from hostname subdomain
 * Examples:
 *   - skg.nextbus.com.gr -> thessaloniki
 *   - skg.localhost -> thessaloniki
 *   - ath.localhost -> athens
 *   - localhost -> null (landing page)
 *   - nextbus.com.gr -> null (landing page)
 */
export function detectCityFromHostname(hostname: string = window.location.hostname): CityId | null {
  // Get the subdomain (first part before first dot)
  const parts = hostname.toLowerCase().split('.')
  const subdomain = parts.length > 1 ? parts[0] : ''
  
  // Skip www
  if (subdomain === 'www') return null
  
  // Check each city for matching subdomain
  for (const city of Object.values(CITIES)) {
    if (city.subdomains.includes(subdomain)) {
      return city.id
    }
  }
  
  // No matching subdomain = landing page (return null)
  return null
}

/**
 * Get city configuration by ID
 */
export function getCityConfig(cityId: CityId): CityConfig {
  return CITIES[cityId] || CITIES.athens
}

/**
 * Get current city based on hostname
 * Returns null if on landing page (no subdomain)
 */
export function getCurrentCity(): CityConfig | null {
  const cityId = detectCityFromHostname()
  if (!cityId) return null
  return getCityConfig(cityId)
}

/**
 * Get the data path for a specific city
 * This is used for importing city-specific JSON data
 */
export function getCityDataPath(cityId: CityId): string {
  return `../data/${cityId}`
}
